import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { hasPendingLocaleSync, isAppLocale } from '@/i18n/config';
import { syncLocaleToBackend, useLocale } from '@/i18n/useLocale';
import { apiClient } from '@/lib/api-client';
import type { paths } from '@/lib/api-types';
import { useTheme } from '@/theme/ThemeProvider';

export type Profile = paths['/v1/profile']['get']['responses'][200]['content']['application/json'];

export type OperatorState =
  | { status: 'loading'; isOperator: false }
  | { status: 'ready'; isOperator: boolean }
  /**
   * The read did not answer, after retries. `isOperator` stays `false` — fail-closed is preserved
   * and is not the thing being changed here.
   *
   * WHAT IS BEING CHANGED, added 2026-09-14. Every failure used to collapse into
   * `{ status: 'ready', isOperator: false }`, which made "this person is not an operator"
   * indistinguishable from "we could not ask". One flaky request, one 503 during a rolling
   * revision, and the platform operator lost the admin section for the whole session — silently,
   * permanently, and while being told they were unauthorized. A third state lets the screen say
   * the true thing instead of the confident wrong one.
   */
  | { status: 'unavailable'; isOperator: false };

const OperatorContext = createContext<OperatorState | null>(null);

function readProfile() {
  return apiClient.GET('/v1/profile');
}
type ProfileRead = Awaited<ReturnType<typeof readProfile>>;

/**
 * RETRIED, because a single attempt made a transient failure permanent for the whole session.
 *
 * Three attempts with a widening pause: enough to ride out a rolling revision or a dropped
 * connection, few enough that a genuinely unreachable backend resolves in about a second rather
 * than hanging the admin gate on a spinner.
 *
 * A 4xx is NOT retried, and that exclusion is the important half. A 4xx is an ANSWER — repeating
 * the same request expecting a different verdict is how a retry loop turns a refusal into an
 * outage, and it would also hammer the backend on every page load for every unauthorized visitor.
 * Only a 5xx or a thrown network error is treated as "did not answer".
 *
 * Exported for its own test: a retry policy nobody can exercise directly is a retry policy nobody
 * knows the shape of.
 */
export async function readProfileWithRetries(
  attemptsLeft = 2,
  wait: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
): Promise<ProfileRead | 'unavailable'> {
  try {
    const result = await readProfile();
    const status = result.response.status;
    if (result.response.ok || (status >= 400 && status < 500)) {
      return result;
    }
  } catch {
    // Network-level failure. Falls through to the retry decision below, same as a 5xx.
  }
  if (attemptsLeft <= 0) {
    return 'unavailable';
  }
  await wait((2 - attemptsLeft) * 200 + 200);
  return readProfileWithRetries(attemptsLeft - 1, wait);
}

/**
 * Client-side operator-detection convenience gate (Resolution 1, SL-0023). Calls `GET /v1/profile`
 * once per signed-in session and treats the caller as the platform-operator when their
 * `roleAssignments` include a Global-scope one — verified against the seeded catalog
 * (`seed-authorization-catalog.ts`), `platform-support` is the ONLY Role seeded at
 * `scopeType: 'global'`, and FR-GA-11 forbids creating a custom Role, so no other Global-scope Role
 * can exist in this MVP. This is a pure UI convenience (plan decision 11) — every `platform/*` route
 * re-derives the caller's own ceiling independently regardless of what this client-side check
 * renders. On any fetch error, `isOperator` resolves to `false` (fail-closed for this convenience
 * gate only), but a failure that could not be ANSWERED now resolves to `unavailable` rather than to
 * a confident `false` — see `OperatorState`. `GET /v1/profile` needs no active-context headers (SL-0022 Resolution 5), so this
 * fetches independently of `ActiveContextProvider`'s own readiness.
 */
export function OperatorProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<OperatorState>({ status: 'loading', isOperator: false });
  const { hydrateFromBackend } = useTheme();
  const { locale } = useLocale();
  const { i18n } = useTranslation();

  useEffect(() => {
    let cancelled = false;

    // Task 12.3 retry-on-next-load (review-gate B3): a locale change made just before the tab
    // closed/reloaded, whose PATCH never confirmed, is retried here — synchronously, ahead of the
    // async profile fetch below — before the backend-locale hydration branch can be honored (that
    // branch's own marker check already blocks adoption while the marker stays set).
    if (hasPendingLocaleSync()) {
      void syncLocaleToBackend(locale);
    }

    void readProfileWithRetries()
      .then((outcome) => {
        if (cancelled) {
          return;
        }
        if (outcome === 'unavailable') {
          setState({ status: 'unavailable', isOperator: false });
          return;
        }
        const { data, error, response } = outcome;
        if (error !== undefined || !response.ok || data === undefined) {
          // A real answer saying no. Distinct from the branch above, and it must stay distinct.
          setState({ status: 'ready', isOperator: false });
          return;
        }
        setState({
          status: 'ready',
          isOperator: data.roleAssignments.some((assignment) => assignment.scopeType === 'global'),
        });
        // Task 12.2: hydrate the persisted theme preference — guarded internally against
        // adopting a stale backend value while a local change's sync is still pending (B3).
        hydrateFromBackend(data.themePreference);
        // Task 12.3: hydrate the persisted locale preference, guarded the same way — never
        // adopted while a local change's own sync is still pending (Resolution 18, as revised).
        if (
          isAppLocale(data.preferredLanguage) &&
          data.preferredLanguage !== i18n.resolvedLanguage &&
          !hasPendingLocaleSync()
        ) {
          void i18n.changeLanguage(data.preferredLanguage);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState({ status: 'unavailable', isOperator: false });
        }
      });

    return () => {
      cancelled = true;
    };
    // `hydrateFromBackend` is wrapped in `useCallback` with a stable, empty dependency array
    // (`ThemeProvider.tsx`), so listing it here does not cause a re-fetch on every render — this
    // fetch still runs exactly once per mount. `locale`/`i18n` are intentionally NOT listed:
    // `i18n` is `i18next`'s own stable singleton instance (its `changeLanguage`/`resolvedLanguage`
    // are read fresh off that same instance whenever the async callback below actually runs, not
    // captured at closure-creation time), and `locale` is read once at mount for the retry-only
    // branch by design, mirroring `ThemeProvider.tsx`'s own mount-only retry effect (Task 12.2).
  }, [hydrateFromBackend]);

  return <OperatorContext.Provider value={state}>{children}</OperatorContext.Provider>;
}

export function useIsOperator(): OperatorState {
  const context = useContext(OperatorContext);
  if (!context) {
    throw new Error('useIsOperator must be used within an OperatorProvider');
  }
  return context;
}
